import { Component, Input }   from '@angular/core';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { NgForm }             from '@angular/forms';
import { TranslateService }   from '@ngx-translate/core';

import { CodeTypeModel }                    from '../core/models/code-type';
import { ConfirmService }                   from '../messages/confirm/confirm.service';
import { MessagesService }                  from '../messages/messages.service';
import { StatementsCustomerModel }          from './typings/statements.customer';
import { StatementsFiltersModel }           from './typings/statements.filters';
import { StatementsSeparateCustomersModel } from './typings/statements.separate-customers';
import { StatementsService }                from './statements.service';
import { Table }                            from '../table/types/table';
import { TableQueryParameters }             from '../table/types/table-query-parameters';
import { TableService }                     from '../table/table.service';

@Component({
  selector   : 'wor-statements',
  template: require('./statements.component.html')
})
export class StatementsComponent {
  @BlockUI('statementsBlock') block : NgBlockUI;

  @Input() constraints: Array<CodeTypeModel>           = [];
  @Input() customers  : Array<StatementsCustomerModel> = [];

  filters : StatementsFiltersModel = this.tableService.setFiltersFromCache(this.statementsService.tableCacheKey, {
    asAt          : null,
    beginDate     : null,
    constraintType: '*',
    endDate       : null,
    includePaid   : false,
    statement     : false,
  });

  selectedCustomers: Array<StatementsCustomerModel> = [];

  tableSettings: Table = {
    broadcast: this.statementsService.broadcastKey,
    cache    : {
      enabled: true,
      key    : this.statementsService.tableCacheKey
    },
    columns: [
      {
        field : 'customer.display_name',
        header: {
          text: this.translateService.instant('CUSTOMER_SPACE.CUSTOMER')
        },
      },
      {
        field : 'customer.email',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.EMAIL')
        },
      },
      {
        field : 'customer.balance',
        header: {
          text: this.translateService.instant('JS_SPACE.COLUMNS.BALANCE')
        },
        pipe: 'globalCurrency'
      },
      {
        field : 'contact_type',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.TYPE')
        },
      }
    ],
    empty: {
      text: this.translateService.instant('GENERAL_SPACE.STATUS.NO_RESULTS')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging(this.statementsService.tableCacheKey).pageSize || 10,
      pageSizes : [ 5, 10, 25, 50 ],
      queryCount: () => this.statementsService.getCustomersListCount(this.filters)
    },
    rows: {
      highlight: {
        enabled: true
      },
      select: {
        enabled: true,
        multi  : true
      }
    },
    sorting: this.tableService.setSortingFromCache(this.statementsService.tableCacheKey, {
      direction: 'asc',
      field    : 'customer.display_name'
    })
  };

  constructor (
    private confirmService   : ConfirmService,
    private messagesService  : MessagesService,
    private statementsService: StatementsService,
    private tableService     : TableService,
    private translateService : TranslateService
  ) {}

  filterCustomer () : void {
    this.selectNone();

    window[ this.tableSettings.broadcast ](this.filters);
  }

  getCustomers ( params : TableQueryParameters ) : Promise<Array<StatementsCustomerModel>> {
    this.block.start();

    return this.statementsService.getCustomersList(this.filters, params)
      .then(( customers : Array<StatementsCustomerModel> ) => {
        this.customers = customers;

        this.updateSelection();

        return this.customers;
      })
      .finally(() => this.block.stop());
  }

  onRowClick ( customer : StatementsCustomerModel ) : void {
    if ( customer.selected ) {
      const exists = this.selectedCustomers.some(c => c.id === customer.customer.id);

      if ( !exists ) {
        this.selectedCustomers.push({
          contact_type: customer.contact_type,
          id          : customer.customer.id,
        });
      }
    }
    else {
      this.selectedCustomers = this.selectedCustomers.filter(c => c.id !== customer.customer.id);
    }
  }

  process ( form : NgForm ) : void {
    if ( this.selectedCustomers.length <= 0 ) {
      this.messagesService.show(this.translateService.instant('CUSTOMER_SPACE.STATEMENTS.NO_CUSTOMERS_SELECTED'), 'error');

      return;
    }

    if ( !this.filters.statement && this.filters.includePaid && !this.filters.beginDate ) {
      this.messagesService.error(this.translateService.instant('GENERAL_SPACE.ERROR_MESSAGE.PROVIDE_BEGIN_DATE'));

      return;
    }

    if ( this.filters.statement && !this.filters.asAt ) {
      this.messagesService.error(this.translateService.instant('GENERAL_SPACE.ERROR_MESSAGE.PROVIDE_AS_AT_DATE'));

      return;
    }

    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.STATEMENTS_SEND_CONFIRM'))
    .then(() => this.block.start())
    .then(() => this.statementsService.separate(this.filters.constraintType, this.selectedCustomers))
    .then(( separateCustomers : StatementsSeparateCustomersModel ) => separateCustomers.email_count
      ? this.statementsService.email(separateCustomers, this.filters)
        .then(( separateCustomers : StatementsSeparateCustomersModel ) => {
          this.messagesService.show(`${
            separateCustomers.email_count
          } ${
            this.translateService.instant('CUSTOMER_SPACE.STATEMENTS.STATEMENTS_EMAILED')
          }`, 'success');

          return separateCustomers;
        })
      : separateCustomers
    )
    .then(( separateCustomers : StatementsSeparateCustomersModel ) => separateCustomers.print_count
      ? this.statementsService.print(separateCustomers, this.filters)
        .then(( url : string ) => {
          this.messagesService.show(`${
            separateCustomers.print_count
          } ${
            this.translateService.instant('CUSTOMER_SPACE.STATEMENTS.STATEMENTS_PRINTED')
          }`, 'success');

          window.open(url);
        })
      : null
    )
    .catch(error => this.messagesService.error(error))
    .finally(() => this.block.stop());
  }

  selectAll () : void {
    this.block.start();

    this.statementsService.getCustomersListCount(this.filters)
    .then(( count : number ) => this.statementsService.getCustomersList(this.filters, {
      pageSize: count
    }))
    .then(( customers : Array<StatementsCustomerModel> ) => {
      this.selectedCustomers = [];

      return customers.forEach(customer => {
        customer.selected = true;

        this.onRowClick(customer);
      });
    })
    .then(() => this.updateSelection())
    .catch(error => this.messagesService.error(error))
    .finally(() => this.block.stop());
  }

  selectNone () : void {
    this.selectedCustomers = [];

    this.updateSelection();
  }

  updateSelection () : void {
    this.customers.forEach(customer => {
      customer.selected = this.selectedCustomers.some(c => c.id === customer.customer.id);
    });
  }
}
